import { useState } from "react";
import { Radio, RadioGroup } from "@headlessui/react";
import { BuildingOfficeIcon, PlusIcon } from "@heroicons/react/24/outline";
import { Controller, useFormContext } from "react-hook-form";
import clsx from "clsx";
import { FACILITY } from "../../../../constants";
import type { ShippingLabelFormValues } from "../../../../types/forms";
import { useUserContext } from "../../../../hooks/useUserContext";
import NewAddressForm from "./newAddressForm";

export default function AddressDetails() {
  const { control } = useFormContext<ShippingLabelFormValues>();
  const { user } = useUserContext();
  const [isAddingAddress, setIsAddingAddress] = useState(false);

  return (
    <section>
      <h2 className="text-lg font-medium text-gray-950 mb-5">
        Address Details
      </h2>

      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-3">
          <p className="text-sm text-gray-500">To</p>
          <div className="flex items-center gap-3 rounded-xl border border-gray-200 p-4">
            <BuildingOfficeIcon className="size-5 shrink-0 text-gray-500" />
            <div className="flex flex-col gap-1.5 min-w-0">
              <p className="text-sm md:text-base font-medium text-gray-950">
                {FACILITY.name}
              </p>
              <p className="text-sm text-gray-500">{FACILITY.address}</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <p className="text-sm text-gray-500">From</p>

          <Controller
            name="fromAddressId"
            control={control}
            rules={{ required: "Select the address you're shipping from" }}
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-5">
                <RadioGroup
                  value={field.value ?? null}
                  onChange={field.onChange}
                  aria-label="From address"
                  className="flex flex-col gap-5"
                >
                  {user?.addresses.map(({ id, label, address }) => (
                    <Radio
                      key={id}
                      value={id}
                      className={clsx(
                        "group flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-colors outline-none",
                        "data-checked:border-blue-500 data-checked:bg-blue-50/50 data-focus:ring-2 data-focus:ring-blue-500",
                        fieldState.error ? "border-red-500" : "border-gray-200",
                      )}
                    >
                      <span className="flex size-5 shrink-0 items-center justify-center rounded-full border-2 border-gray-300 group-data-checked:border-blue-600">
                        <span className="size-2.5 rounded-full bg-blue-600 opacity-0 group-data-checked:opacity-100" />
                      </span>
                      <span className="flex flex-col gap-1.5 min-w-0">
                        <span className="text-sm md:text-base font-medium text-gray-950">
                          {label}
                        </span>
                        <span className="text-sm text-gray-500">
                          {[
                            address.street,
                            address.unit,
                            address.city,
                            `${address.state} ${address.zip}`,
                            "United States",
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </span>
                      </span>
                    </Radio>
                  ))}
                </RadioGroup>

                {fieldState.error && (
                  <p className="text-sm text-red-500">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />

          <div className="mt-2">
            {isAddingAddress ? (
              <NewAddressForm onCancel={() => setIsAddingAddress(false)} />
            ) : (
              <button
                type="button"
                onClick={() => setIsAddingAddress(true)}
                className="flex w-fit items-center gap-2 rounded-xl border border-gray-200 px-5 h-13 text-base font-medium text-gray-950 hover:bg-blue-50 cursor-pointer transition-colors"
              >
                <PlusIcon className="size-5" />
                Add new address
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
